import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import ExecutiveCard from '../../../components/executive/ExecutiveCard'
import ExecutiveSection from '../../../components/executive/ExecutiveSection'
import LoadingOrbit from '../../../components/LoadingOrbit'
import usePlatformSettings from '../../../hooks/usePlatformSettings'
import { formatMoneyFull } from '../../../utils/formatters'
import { getCurrentPlanningCycle, getPlanningContext, getProject, getScenarioContext, saveProject } from './api'
import { listInvestmentProposals } from '../investment_proposal/api'

const empty = {
    project_name: '',
    project_description: '',
    estimated_cost: '',
    management_justification: '',
    scenario_allocations: []
}
const pct = (value) => Number(value || 0)
const allocationJustificationMessage = 'Allocation justification is required for each selected scenario.'
const allocationJustificationSummary =
    'Project could not be registered. Complete the required allocation justification for each selected scenario.'
const writableProjectPayload = (project) => ({
    project_name: project.project_name,
    project_description: project.project_description,
    estimated_cost: project.estimated_cost,
    management_justification: project.management_justification,
    scenario_allocations: project.scenario_allocations.map((allocation) => ({
        scenario_reference: allocation.scenario_reference,
        allocation_percentage: allocation.allocation_percentage,
        allocation_justification: allocation.allocation_justification
    }))
})
const valueOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null
    const number = Number(value)
    return Number.isFinite(number) ? number : null
}

const statusBadgeStyles = {
    Planned: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    Partial: 'bg-blue-50 text-blue-700 ring-blue-200',
    'Above Recommended': 'bg-violet-50 text-violet-700 ring-violet-200',
    'Not Planned': 'bg-slate-50 text-slate-600 ring-slate-200'
}

function Badge({ children, className = '' }) {
    return (
        <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${className}`}
        >
            {children}
        </span>
    )
}

function PriorityBadge({ value }) {
    if (!value) return <span className="text-slate-400">Unavailable</span>
    return <Badge className="bg-slate-50 text-slate-600 ring-slate-200">{value}</Badge>
}

function StatusBadge({ value }) {
    return <Badge className={statusBadgeStyles[value] || statusBadgeStyles['Not Planned']}>{value}</Badge>
}

function investmentStatus(optimalInvestment, alreadyPlanned) {
    const optimal = valueOrNull(optimalInvestment)
    const planned = valueOrNull(alreadyPlanned) || 0

    if (optimal !== null && optimal > 0) {
        if (planned > optimal) return 'Above Recommended'
        if (planned === optimal) return 'Planned'
        if (planned > 0) return 'Partial'
        return 'Not Planned'
    }

    if (optimal === 0 && planned > 0) return 'Above Recommended'
    if (planned > 0) return 'Partial'
    return 'Not Planned'
}

function remainingInvestment(optimalInvestment, alreadyPlanned) {
    const optimal = valueOrNull(optimalInvestment)
    const planned = valueOrNull(alreadyPlanned) || 0

    if (optimal === null) return null
    return optimal - planned
}

function moneyOrUnavailable(value, currency) {
    const number = valueOrNull(value)
    return number === null ? 'Unavailable' : formatMoneyFull(number, currency)
}

export default function InvestmentProjectWorkspace() {
    const { projectReference } = useParams()
    const [searchParams] = useSearchParams()
    const requestedReturn = searchParams.get('returnTo')
    const requestedCycleReference = searchParams.get('planningCycle')
    const proposalReturn = /^\/investment-proposals\/[0-9a-f-]+$/i.test(requestedReturn || '') ? requestedReturn : null
    const { settings } = usePlatformSettings()
    const currency = settings.currency || 'USD'
    const [project, setProject] = useState(empty)
    const [scenarios, setScenarios] = useState([])
    const [projects, setProjects] = useState([])
    const [proposalDraft, setProposalDraft] = useState(null)
    const [planningContext, setPlanningContext] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [allocationErrors, setAllocationErrors] = useState({})
    const [saved, setSaved] = useState('')
    const cycleReference = planningContext?.cycle_reference || null

    const installPlanningContext = useCallback((capitalContext, selectedProjectReference = projectReference) => {
        const authoritativeProjects = capitalContext.projects || []
        setProjects(authoritativeProjects)
        setPlanningContext(capitalContext)
        if (selectedProjectReference) {
            const authoritativeProject = authoritativeProjects.find(
                (item) => String(item.project_reference) === String(selectedProjectReference)
            )
            if (authoritativeProject) {
                setProject({
                    ...empty,
                    ...authoritativeProject,
                    scenario_allocations: authoritativeProject.scenario_allocations || []
                })
            }
        }
    }, [projectReference])

    const refreshPlanningContext = useCallback(async (reference, selectedProjectReference = projectReference) => {
        const capitalContext = await getPlanningContext(reference)
        installPlanningContext(capitalContext, selectedProjectReference)
        return capitalContext
    }, [installPlanningContext, projectReference])

    useEffect(() => {
        let on = true
        async function load() {
            try {
                setLoading(true)
                setError('')
                const activeCycle = await getCurrentPlanningCycle()
                if (requestedCycleReference && requestedCycleReference !== activeCycle.cycle_reference) {
                    throw new Error('The selected Planning cycle is no longer authoritative. Return to the current Investment Planning cycle before editing.')
                }
                const [ctx, capitalContext, proposals] = await Promise.all([
                    getScenarioContext(),
                    getPlanningContext(activeCycle.cycle_reference),
                    listInvestmentProposals()
                ])
                const current = projectReference
                    ? await getProject(activeCycle.cycle_reference, projectReference)
                    : empty
                if (!on) return
                setScenarios(ctx)
                setProject({ ...empty, ...(current || {}), scenario_allocations: current?.scenario_allocations || [] })
                setProposalDraft(proposals.find((item) => item.status === 'DRAFT' && item.planning_cycle_reference === activeCycle.cycle_reference) || null)
                installPlanningContext(capitalContext, null)
            } catch (e) {
                if (on) {
                    setError(
                        e.response?.status === 403
                            ? 'Authentication or permission error loading Investment Planning.'
                            : e.response?.status === 404
                              ? 'No authoritative active Investment Planning cycle exists. Return to Portfolio Capital Strategy and complete the governed handoff.'
                              : e.message || 'Unable to load Investment Planning.'
                    )
                }
            } finally {
                if (on) setLoading(false)
            }
        }
        load()
        return () => {
            on = false
        }
    }, [installPlanningContext, projectReference, requestedCycleReference])

    const estimated = Number(project.estimated_cost || 0)
    const totalPct = useMemo(
        () => project.scenario_allocations.reduce((sum, allocation) => sum + pct(allocation.allocation_percentage), 0),
        [project]
    )
    const amount = (estimated * totalPct) / 100
    const complete = project.scenario_allocations.length > 0 && Math.abs(totalPct - 100) < 0.0001
    const constrainedResponse = planningContext?.constrained_optimization_evidence?.constrainedResponse
    const recommendedScenarios = useMemo(() => {
        if (!constrainedResponse?.allocation) return []
        return constrainedResponse.allocation.map((allocation) => {
            const context =
                scenarios.find((scenario) => String(scenario.scenario_reference) === String(allocation.scenario_id)) ||
                {}
            return {
                ...context,
                scenario_reference: allocation.scenario_id,
                scenario_name: allocation.scenario_name || context.scenario_name,
                modeled_allocation: allocation.allocated_investment,
                currently_allocated_project_investment: projects.reduce((sum, item) => sum + (item.scenario_allocations || []).filter((row) => String(row.scenario_reference) === String(allocation.scenario_id)).reduce((subtotal, row) => subtotal + Number(row.allocated_amount || 0), 0), 0)
            }
        })
    }, [constrainedResponse, scenarios, projects])
    const summary = planningContext?.planning_summary
    const recommendedInvestment = valueOrNull(summary?.management_constrained_modeled_allocation)
    const allocationScenarios = constrainedResponse
        ? recommendedScenarios
        : scenarios.map((scenario) => ({ ...scenario, modeled_allocation: null }))
    const registeredInvestment = valueOrNull(summary?.registered_project_investment)
    const requestedInvestment = valueOrNull(summary?.management_requested_investment)
    const remainingToSubstantiate = valueOrNull(summary?.remaining_to_substantiate)
    const requestSubstantiated = valueOrNull(summary?.request_substantiated_percentage)
    const plannedScenarios = Number(summary?.scenarios_planned || 0)

    const update = (key, value) => setProject((current) => ({ ...current, [key]: value }))
    const toggle = (id) => {
        setAllocationErrors((current) => {
            const next = { ...current }
            delete next[String(id)]
            return next
        })
        setProject((current) => {
            const exists = current.scenario_allocations.some(
                (allocation) => String(allocation.scenario_reference) === String(id)
            )
            return {
                ...current,
                scenario_allocations: exists
                    ? current.scenario_allocations.filter(
                          (allocation) => String(allocation.scenario_reference) !== String(id)
                      )
                    : [
                          ...current.scenario_allocations,
                          { scenario_reference: id, allocation_percentage: '', allocation_justification: '' }
                      ]
            }
        })
    }
    const updateAlloc = (id, key, value) => {
        if (key === 'allocation_justification') {
            setAllocationErrors((current) => {
                const next = { ...current }
                delete next[String(id)]
                return next
            })
        }
        setProject((current) => ({
            ...current,
            scenario_allocations: current.scenario_allocations.map((allocation) =>
                String(allocation.scenario_reference) === String(id) ? { ...allocation, [key]: value } : allocation
            )
        }))
    }

    const justificationErrorsFromResponse = (data) => {
        const nested = data?.scenario_allocations
        if (!Array.isArray(nested)) return {}
        return nested.reduce((errors, item, index) => {
            if (item?.allocation_justification && project.scenario_allocations[index]) {
                errors[String(project.scenario_allocations[index].scenario_reference)] = allocationJustificationMessage
            }
            return errors
        }, {})
    }

    const safeApiError = (data) => {
        const failure = projectReference ? 'Project could not be updated.' : 'Project could not be registered.'
        if (typeof data === 'string' && data.trim()) return `${failure} ${data.trim()}`
        if (typeof data?.detail === 'string' && data.detail.trim())
            return `${failure} ${data.detail.trim()}`
        const messages = []
        const collect = (value) => {
            if (typeof value === 'string' && value.trim()) messages.push(value.trim())
            else if (Array.isArray(value)) value.forEach(collect)
            else if (value && typeof value === 'object') Object.values(value).forEach(collect)
        }
        collect(data)
        return messages.length
            ? `${failure} ${[...new Set(messages)].join(' ')}`
            : `${failure} Review the entered information and try again.`
    }

    async function onSave() {
        const missingJustifications = project.scenario_allocations.reduce((errors, allocation) => {
            if (!String(allocation.allocation_justification || '').trim()) {
                errors[String(allocation.scenario_reference)] = allocationJustificationMessage
            }
            return errors
        }, {})
        if (Object.keys(missingJustifications).length) {
            setAllocationErrors(missingJustifications)
            setSaved('')
            setError(allocationJustificationSummary)
            return
        }

        let createdProjectReference = null
        try {
            setError('')
            setSaved('')
            setAllocationErrors({})
            const data = await saveProject(
                cycleReference,
                writableProjectPayload(project),
                projectReference
            )
            if (projectReference) {
                await refreshPlanningContext(cycleReference, projectReference)
                setSaved('Investment project updated.')
            } else {
                createdProjectReference = data.project_reference
                await refreshPlanningContext(cycleReference, createdProjectReference)
                setSaved('Investment project saved.')
            }
        } catch (e) {
            if (createdProjectReference) {
                setError('Investment project saved, but Planning context could not be refreshed.')
                return
            }
            const responseAllocationErrors = justificationErrorsFromResponse(e.response?.data)
            if (Object.keys(responseAllocationErrors).length) {
                setAllocationErrors(responseAllocationErrors)
                setError(allocationJustificationSummary)
            } else {
                setError(safeApiError(e.response?.data || e.message))
            }
        }
    }

    if (loading) {
        return (
            <main className="p-6" aria-busy="true">
                <LoadingOrbit
                    message="Loading Investment Planning..."
                    submessage="Preparing investment strategy workspace"
                />
            </main>
        )
    }

    if (!planningContext?.cycle_reference) {
        return <main className="min-h-screen bg-slate-50 p-6"><div className="mx-auto max-w-3xl rounded-2xl border border-amber-300 bg-amber-50 p-6" role="alert"><h1 className="text-xl font-bold text-slate-950">Investment Planning is unavailable</h1><p className="mt-2 text-amber-950">{error || 'No authoritative active Investment Planning cycle exists. Complete the governed Portfolio Capital Strategy handoff before planning.'}</p><Link className="mt-4 inline-block font-semibold text-blue-700 underline" to="/portfolio-decision">Return to Portfolio Capital Strategy</Link></div></main>
    }

    return (
        <main className="min-h-screen bg-slate-50 p-6">
            <div className="space-y-6" data-testid="investment-planning-fluid-owner">
                <header className="flex flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-start">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
                            Investment Governance
                        </p>
                        <h1 className="mt-2 text-3xl font-bold text-slate-900">Digital Risk Investment Planning</h1>
                        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                            The investment need has already been identified through FAIR and Investment Optimization.
                            Use this workspace to register the Management-defined projects that will implement that
                            investment strategy.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <Link
                            className="inline-flex items-center justify-center rounded-xl border border-blue-700 px-4 py-2 text-sm font-semibold text-blue-700"
                            to={
                                proposalDraft
                                    ? `/investment-proposals/${proposalDraft.proposal_reference}`
                                    : cycleReference ? `/investment-proposals/new?planningCycle=${encodeURIComponent(cycleReference)}` : '/investment-proposals/new'
                            }
                        >
                            {proposalDraft ? 'Open Investment Proposal' : 'Create Investment Proposal'}
                        </Link>
                        <Link
                            className="inline-flex items-center justify-center rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800"
                            to={`/investment-projects/new?planningCycle=${encodeURIComponent(cycleReference)}`}
                        >
                            Register Project
                        </Link>
                    </div>
                </header>
                {error && (
                    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        {error}
                    </div>
                )}
                {saved && (
                    <div
                        role="status"
                        className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700"
                    >
                        {saved}
                        {proposalReturn && (
                            <Link className="ml-3 font-semibold underline" to={proposalReturn}>
                                Return to Investment Proposal
                            </Link>
                        )}
                    </div>
                )}

                <ExecutiveSection
                    title="Capital Planning Position"
                    subtitle="Distinct analytical and Management capital authorities for project substantiation."
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <ExecutiveCard
                            title="Management-Constrained Modeled Allocation"
                            value={moneyOrUnavailable(recommendedInvestment, currency)}
                            subtitle="Same-authority optimizer evidence under Management Requested Capital; not authorization."
                            accent="info"
                        />
                        <ExecutiveCard
                            title="Management Requested Investment"
                            value={moneyOrUnavailable(requestedInvestment, currency)}
                            subtitle="Capital Management intends to substantiate through executable projects and formally request."
                            accent="info"
                        />
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-3">
                        <ExecutiveCard
                            title="Registered Project Investment"
                            value={moneyOrUnavailable(registeredInvestment, currency)}
                            subtitle="Aggregate estimated cost of registered projects."
                        />
                        <ExecutiveCard
                            title="Remaining to Substantiate"
                            value={moneyOrUnavailable(remainingToSubstantiate, currency)}
                            subtitle="Requested investment minus registered project cost; a planning completeness measure, including a negative value when projects exceed the request."
                        />
                        <ExecutiveCard
                            title="Request Substantiated %"
                            value={
                                requestSubstantiated === null ? 'Unavailable' : `${requestSubstantiated.toFixed(1)}%`
                            }
                            subtitle="Share of Management Requested Investment represented by registered projects."
                        />
                    </div>
                </ExecutiveSection>

                <ExecutiveSection
                    title="Recommended Investment Strategy"
                    subtitle="Management-constrained modeled allocation from the accepted Portfolio Capital Optimization authority. Unavailable rather than replaced by Strategic Potential when evidence is absent."
                >
                    {!constrainedResponse && (
                        <div
                            role="status"
                            className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                        >
                            Authoritative Management-constrained evidence is unavailable. Reevaluate Portfolio Capital
                            Strategy before planning; no legacy or Strategic Potential fallback is applied.
                        </div>
                    )}
                    <div className="overflow-x-auto" data-testid="investment-planning-table-scroll">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                                <tr>
                                    <th className="py-3 pr-4">Scenario</th>
                                    <th className="px-4 py-3">Technical priority</th>
                                    <th className="px-4 py-3">Modeled Management-Constrained Allocation</th>
                                    <th className="px-4 py-3">Already planned</th>
                                    <th className="px-4 py-3">Remaining to substantiate</th>
                                    <th className="py-3 pl-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {recommendedScenarios.map((scenario) => {
                                    const remaining = remainingInvestment(
                                        scenario.modeled_allocation,
                                        scenario.currently_allocated_project_investment
                                    )
                                    const status = investmentStatus(
                                        scenario.modeled_allocation,
                                        scenario.currently_allocated_project_investment
                                    )
                                    return (
                                        <tr key={scenario.scenario_reference} className="align-top hover:bg-slate-50">
                                            <td className="py-4 pr-4 font-medium text-slate-900">
                                                {scenario.scenario_reference} — {scenario.scenario_name}
                                                <div className="mt-1 text-xs font-normal text-slate-500">
                                                    {scenario.asset_name}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <PriorityBadge value={scenario.technical_priority} />
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                {moneyOrUnavailable(scenario.modeled_allocation, currency)}
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                {moneyOrUnavailable(
                                                    scenario.currently_allocated_project_investment,
                                                    currency
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                {moneyOrUnavailable(remaining, currency)}
                                            </td>
                                            <td className="py-4 pl-4">
                                                <StatusBadge value={status} />
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </ExecutiveSection>

                <ExecutiveSection
                    title="Investment Projects"
                    subtitle="Register the Management-defined project and the justification for the investment."
                >
                    <div className="grid gap-4 lg:grid-cols-2">
                        <input
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            aria-label="Project name"
                            placeholder="Project name"
                            value={project.project_name || ''}
                            onChange={(e) => update('project_name', e.target.value)}
                        />
                        <input
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            aria-label="Estimated cost"
                            type="number"
                            min="0"
                            placeholder="Estimated cost"
                            value={project.estimated_cost || ''}
                            onChange={(e) => update('estimated_cost', e.target.value)}
                        />
                        <textarea
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm lg:col-span-2"
                            aria-label="Description"
                            placeholder="Description"
                            value={project.project_description || ''}
                            onChange={(e) => update('project_description', e.target.value)}
                        />
                        <textarea
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm lg:col-span-2"
                            aria-label="Management justification"
                            placeholder="Management justification"
                            value={project.management_justification || ''}
                            onChange={(e) => update('management_justification', e.target.value)}
                        />
                    </div>
                </ExecutiveSection>

                <ExecutiveSection
                    title="Allocate Project Cost to FAIR Scenarios"
                    subtitle="Allocate the project estimate across the scenarios it is intended to improve. The optimizer allocation is contextual guidance and does not overwrite project planning data."
                >
                    {!constrainedResponse && (
                        <div role="status" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                            Management-constrained allocation guidance is unavailable or incomplete. Existing project allocations are preserved; no legacy or Strategic Potential recommendation is substituted.
                        </div>
                    )}
                    <div className="overflow-x-auto" data-testid="investment-planning-table-scroll">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                                <tr>
                                    <th className="py-3 pr-4">Select</th>
                                    <th className="px-4 py-3">Scenario</th>
                                    <th className="px-4 py-3">Technical priority</th>
                                    <th className="px-4 py-3">Management-constrained allocation</th>
                                    <th className="px-4 py-3">Allocation %</th>
                                    <th className="px-4 py-3">Allocated amount</th>
                                    <th className="py-3 pl-4">Allocation justification</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {allocationScenarios.map((s) => {
                                    const a = project.scenario_allocations.find(
                                        (x) => String(x.scenario_reference) === String(s.scenario_reference)
                                    )
                                    const rowAmount = (estimated * pct(a?.allocation_percentage)) / 100
                                    const justificationError = allocationErrors[String(s.scenario_reference)]
                                    const errorId = `allocation-justification-error-${s.scenario_reference}`
                                    return (
                                        <tr key={s.scenario_reference} className="align-top hover:bg-slate-50">
                                            <td className="py-4 pr-4">
                                                <input
                                                    className="h-4 w-4 rounded border-slate-300 text-blue-700"
                                                    aria-label={`Select ${s.scenario_name}`}
                                                    type="checkbox"
                                                    checked={!!a}
                                                    onChange={() => toggle(s.scenario_reference)}
                                                />
                                            </td>
                                            <td className="px-4 py-4 font-medium text-slate-900">
                                                {s.scenario_reference} — {s.scenario_name}
                                                <div className="mt-1 text-xs font-normal text-slate-500">
                                                    {s.asset_name}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <PriorityBadge value={s.technical_priority} />
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                {moneyOrUnavailable(s.modeled_allocation, currency)}
                                            </td>
                                            <td className="px-4 py-4">
                                                <input
                                                    className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-sm disabled:bg-slate-100"
                                                    aria-label={`Allocation percentage for ${s.scenario_name}`}
                                                    disabled={!a}
                                                    type="number"
                                                    value={a?.allocation_percentage || ''}
                                                    onChange={(e) =>
                                                        updateAlloc(
                                                            s.scenario_reference,
                                                            'allocation_percentage',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">
                                                {formatMoneyFull(rowAmount, currency)}
                                            </td>
                                            <td className="py-4 pl-4">
                                                <input
                                                    className={`min-w-64 rounded-lg border px-2 py-1 text-sm disabled:bg-slate-100 ${
                                                        justificationError ? 'border-red-500' : 'border-slate-200'
                                                    }`}
                                                    aria-label={`Allocation justification for ${s.scenario_name}`}
                                                    aria-invalid={justificationError ? 'true' : undefined}
                                                    aria-describedby={justificationError ? errorId : undefined}
                                                    disabled={!a}
                                                    value={a?.allocation_justification || ''}
                                                    onChange={(e) =>
                                                        updateAlloc(
                                                            s.scenario_reference,
                                                            'allocation_justification',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                {justificationError && (
                                                    <p id={errorId} className="mt-1 text-xs font-medium text-red-700">
                                                        {justificationError}
                                                    </p>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </ExecutiveSection>

                <section>
                    <h2 className="mb-3 text-xl font-semibold text-slate-900">Planning Summary</h2>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <ExecutiveCard
                            title="Management-Constrained Modeled Allocation"
                            value={
                                recommendedInvestment !== null
                                    ? formatMoneyFull(recommendedInvestment, currency)
                                    : 'Unavailable'
                            }
                            subtitle="Backend-returned planning recommendation."
                            accent="info"
                        />
                        <ExecutiveCard
                            title="Registered Project Investment"
                            value={formatMoneyFull(registeredInvestment, currency)}
                            subtitle="Estimated cost of registered, non-archived projects."
                            accent="success"
                        />
                        <ExecutiveCard
                            title="Request Substantiated %"
                            value={requestSubstantiated == null ? 'Unavailable' : `${requestSubstantiated.toFixed(1)}%`}
                            subtitle="Registered project cost as a share of Management Requested Investment."
                            accent="warning"
                        />
                        <ExecutiveCard
                            title="Scenarios Planned"
                            value={
                                constrainedResponse
                                    ? `${plannedScenarios}/${summary?.scenarios_total ?? recommendedScenarios.length}`
                                    : 'Unavailable'
                            }
                            subtitle="Scenarios whose registered allocation meets the Management-constrained recommendation."
                            accent="default"
                        />
                    </div>
                    <div className="mt-4 grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm md:grid-cols-5">
                        <p>
                            <span className="block text-xs uppercase tracking-wide text-slate-400">
                                Project estimated cost
                            </span>
                            <strong className="text-slate-900">{formatMoneyFull(estimated, currency)}</strong>
                        </p>
                        <p>
                            <span className="block text-xs uppercase tracking-wide text-slate-400">
                                Percentage allocated
                            </span>
                            <strong className="text-slate-900">{totalPct.toFixed(2)}%</strong>
                        </p>
                        <p>
                            <span className="block text-xs uppercase tracking-wide text-slate-400">
                                Amount allocated
                            </span>
                            <strong className="text-slate-900">{formatMoneyFull(amount, currency)}</strong>
                        </p>
                        <p>
                            <span className="block text-xs uppercase tracking-wide text-slate-400">
                                Amount unallocated
                            </span>
                            <strong className="text-slate-900">
                                {formatMoneyFull(Math.max(0, estimated - amount), currency)}
                            </strong>
                        </p>
                        <p>
                            <span className="block text-xs uppercase tracking-wide text-slate-400">
                                Allocation status
                            </span>
                            <strong className="text-slate-900">
                                {complete ? 'Allocation complete' : 'Incomplete allocation.'}
                            </strong>
                        </p>
                    </div>
                </section>
                <div className="flex gap-3">
                    <button
                        className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800"
                        onClick={onSave}
                    >
                        Save
                    </button>
                    <button
                        className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                        onClick={onSave}
                    >
                        Update
                    </button>
                </div>
                {projects.length > 0 && (
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                        <h2 className="font-semibold text-slate-900">Saved projects</h2>
                        {projects.map((p) => (
                            <Link
                                className="mt-2 block text-sm font-medium text-blue-700 underline"
                                key={p.project_reference}
                                to={`/investment-projects/${p.project_reference}?planningCycle=${encodeURIComponent(cycleReference)}`}
                            >
                                {p.project_name}
                            </Link>
                        ))}
                    </section>
                )}
            </div>
        </main>
    )
}
